import React, { useEffect, useState, useRef } from 'react';
import api from '../utils/api';
import { Plus, Edit, Trash2, Search, Upload, FileSpreadsheet, X, CheckCircle, XCircle } from 'lucide-react';

const Specifications = () => {
  const [activeTab, setActiveTab] = useState('specs');
  const [specs, setSpecs] = useState([]);
  const [qcResults, setQcResults] = useState([]);
  const [qcLogs, setQcLogs] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const emptyForm = { product_sku: '', product_name: '', spec_name: '', version: '1.0', parameters: [] };
  const [formData, setFormData] = useState({ ...emptyForm });
  const [paramInput, setParamInput] = useState({ name: '', unit: '', min_value: '', max_value: '', target_value: '', test_method: '' });
  const [resultForm, setResultForm] = useState({ product_sku: '', product_name: '', test_results: [], remarks: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sRes, rRes, lRes, iRes] = await Promise.all([
        api.get('/specs/'), api.get('/specs/qc-results'), api.get('/specs/qc-logs'), api.get('/sales/inventory')
      ]);
      setSpecs(sRes.data); setQcResults(rRes.data); setQcLogs(lRes.data); setInventory(iRes.data);
    } catch(e) { console.error(e); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm }); setShowModal(true); };
  const openEdit = (s) => { setEditing(s); setFormData({ product_sku: s.product_sku, product_name: s.product_name || '', spec_name: s.spec_name, version: s.version || '1.0', parameters: s.parameters || [] }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/specs/${editing.id}`, formData);
      else await api.post('/specs/', formData);
      setShowModal(false); fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const deleteSpec = async (id, name) => {
    if (!window.confirm(`Delete spec "${name}"?`)) return;
    try { await api.delete(`/specs/${id}`); fetchData(); } catch(e) { alert('Failed'); }
  };

  const addParam = () => {
    if (!paramInput.name) return;
    setFormData({ ...formData, parameters: [...formData.parameters, { ...paramInput }] });
    setParamInput({ name: '', unit: '', min_value: '', max_value: '', target_value: '', test_method: '' });
  };

  const removeParam = (idx) => { setFormData({ ...formData, parameters: formData.parameters.filter((_, i) => i !== idx) }); };

  const onProductSelect = (sku) => {
    const inv = inventory.find(i => i.sku === sku);
    setFormData({ ...formData, product_sku: sku, product_name: inv?.name || '' });
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/specs/import-excel', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert(`Imported ${res.data.imported_count} specifications`);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Import failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  // QC Test Result
  const openQcResult = (spec) => {
    const testParams = (spec?.parameters || []).map(p => ({ name: p.name, unit: p.unit, target_value: p.target_value, min_value: p.min_value, max_value: p.max_value, actual_value: '', status: 'pending' }));
    setResultForm({ product_sku: spec?.product_sku || '', product_name: spec?.product_name || '', test_results: testParams, remarks: '' });
    setShowResultModal(true);
  };

  const submitQcResult = async (e) => {
    e.preventDefault();
    try {
      await api.post('/specs/qc-results', resultForm);
      setShowResultModal(false); fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const filtered = specs.filter(s => (s.product_sku || '').toLowerCase().includes(searchTerm.toLowerCase()) || (s.spec_name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (s.product_name || '').toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div data-testid="specifications-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Specifications & QC</h1>
      </div>

      <div className="flex gap-2 mb-4">
        {[{ key: 'specs', label: 'Specifications' }, { key: 'results', label: 'QC Test Results' }, { key: 'logs', label: 'QC Logs' }].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} className={`btn btn-sm ${activeTab === t.key ? 'btn-primary' : 'btn-secondary'}`} data-testid={`tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {activeTab === 'specs' && (
        <>
          <div className="card mb-4">
            <div className="flex gap-4" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
                <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
                <input type="text" className="form-input" placeholder="Search specs..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-specs" />
              </div>
              <input type="file" ref={fileInputRef} accept=".xlsx,.xls" onChange={handleExcelUpload} style={{ display: 'none' }} />
              <button onClick={() => fileInputRef.current?.click()} className="btn btn-secondary btn-sm" disabled={uploading} data-testid="import-excel-btn"><Upload size={14} /> {uploading ? 'Importing...' : 'Import Excel'}</button>
              <button onClick={openCreate} className="btn btn-primary btn-sm" data-testid="add-spec-btn"><Plus size={16} /> Add Spec</button>
            </div>
          </div>
          <div className="card">
            {loading ? <div>Loading...</div> : (
              <div className="table-container">
                <table>
                  <thead><tr><th>Product SKU</th><th>Product Name</th><th>Spec Name</th><th>Version</th><th>Parameters</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {filtered.length === 0 ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No specs found</td></tr> : filtered.map(s => (
                      <tr key={s.id} data-testid={`spec-row-${s.id}`}>
                        <td className="font-mono">{s.product_sku}</td>
                        <td>{s.product_name}</td>
                        <td style={{ fontWeight: 500 }}>{s.spec_name}</td>
                        <td>{s.version}</td>
                        <td>{(s.parameters || []).length} params</td>
                        <td><span className={`badge ${s.status === 'active' ? 'badge-success' : 'badge-danger'}`}>{s.status}</span></td>
                        <td>
                          <div className="flex gap-2">
                            <button onClick={() => openQcResult(s)} className="btn btn-ghost btn-sm" title="Run QC Test" data-testid={`qc-btn-${s.id}`}><FileSpreadsheet size={14} color="#0047AB" /></button>
                            <button onClick={() => openEdit(s)} className="btn btn-ghost btn-sm"><Edit size={14} /></button>
                            <button onClick={() => deleteSpec(s.id, s.spec_name)} className="btn btn-ghost btn-sm"><Trash2 size={14} color="#DC2626" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'results' && (
        <div className="card">
          {loading ? <div>Loading...</div> : (
            <div className="table-container">
              <table>
                <thead><tr><th>Product</th><th>Spec</th><th>Tests</th><th>Result</th><th>Tested By</th><th>Date</th></tr></thead>
                <tbody>
                  {qcResults.length === 0 ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No test results</td></tr> : qcResults.map(r => (
                    <tr key={r.id} data-testid={`result-row-${r.id}`}>
                      <td><div className="font-mono">{r.product_sku}</div><div className="text-xs text-muted">{r.product_name}</div></td>
                      <td>{r.spec_name || '-'}</td>
                      <td>
                        <div className="flex gap-1">
                          {(r.test_results || []).map((t, i) => (
                            <span key={i} className={`badge ${t.status === 'pass' ? 'badge-success' : 'badge-danger'}`} style={{ fontSize: '10px' }}>{t.name}</span>
                          ))}
                        </div>
                      </td>
                      <td><span className={`badge ${r.overall_result === 'pass' ? 'badge-success' : 'badge-danger'}`}>{r.overall_result}</span></td>
                      <td>{r.tested_by_name}</td>
                      <td className="text-sm">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="card">
          {loading ? <div>Loading...</div> : (
            <div className="table-container">
              <table>
                <thead><tr><th>Type</th><th>Number/Product</th><th>Status/Result</th><th>Items/Params</th><th>Date</th></tr></thead>
                <tbody>
                  {qcLogs.length === 0 ? <tr><td colSpan={5} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No QC logs</td></tr> : qcLogs.map((l, i) => (
                    <tr key={i}>
                      <td><span className={`badge ${l.type === 'inspection' ? 'badge-info' : 'badge-warning'}`}>{l.type === 'inspection' ? 'Inspection' : 'Test Result'}</span></td>
                      <td className="font-mono">{l.number || l.product_sku || '-'}</td>
                      <td><span className={`badge ${(l.status === 'passed' || l.result === 'pass') ? 'badge-success' : 'badge-danger'}`}>{l.status || l.result}</span></td>
                      <td>{l.items_count || l.params_count || '-'}</td>
                      <td className="text-sm">{l.date ? new Date(l.date).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Spec Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="spec-modal">
          <div className="modal-content" style={{ maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? 'Edit Specification' : 'Create Specification'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Product *</label>
                    <select className="form-select" value={formData.product_sku} onChange={(e) => onProductSelect(e.target.value)} required data-testid="spec-product-select">
                      <option value="">Select Product</option>
                      {inventory.map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Spec Name *</label><input type="text" className="form-input" value={formData.spec_name} onChange={(e) => setFormData({...formData, spec_name: e.target.value})} required data-testid="spec-name" /></div>
                </div>
                <div className="form-group"><label className="form-label">Version</label><input type="text" className="form-input" value={formData.version} onChange={(e) => setFormData({...formData, version: e.target.value})} style={{ width: '100px' }} /></div>

                <div style={{ marginTop: '12px' }}>
                  <label className="form-label" style={{ fontWeight: 600 }}>Parameters</label>
                  <div className="flex gap-2 mb-2" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <input type="text" className="form-input" placeholder="Parameter name" value={paramInput.name} onChange={(e) => setParamInput({...paramInput, name: e.target.value})} style={{ flex: 2 }} data-testid="param-name" />
                    <input type="text" className="form-input" placeholder="Unit" value={paramInput.unit} onChange={(e) => setParamInput({...paramInput, unit: e.target.value})} style={{ flex: 1 }} />
                    <input type="text" className="form-input" placeholder="Min" value={paramInput.min_value} onChange={(e) => setParamInput({...paramInput, min_value: e.target.value})} style={{ flex: 1 }} />
                    <input type="text" className="form-input" placeholder="Target" value={paramInput.target_value} onChange={(e) => setParamInput({...paramInput, target_value: e.target.value})} style={{ flex: 1 }} />
                    <input type="text" className="form-input" placeholder="Max" value={paramInput.max_value} onChange={(e) => setParamInput({...paramInput, max_value: e.target.value})} style={{ flex: 1 }} />
                    <input type="text" className="form-input" placeholder="Method" value={paramInput.test_method} onChange={(e) => setParamInput({...paramInput, test_method: e.target.value})} style={{ flex: 1 }} />
                    <button type="button" onClick={addParam} className="btn btn-secondary btn-sm" data-testid="add-param-btn"><Plus size={14} /></button>
                  </div>
                  {formData.parameters.length > 0 && (
                    <div className="table-container" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <table>
                        <thead><tr><th>Name</th><th>Unit</th><th>Min</th><th>Target</th><th>Max</th><th>Method</th><th></th></tr></thead>
                        <tbody>
                          {formData.parameters.map((p, i) => (
                            <tr key={i}><td>{p.name}</td><td>{p.unit}</td><td>{p.min_value}</td><td>{p.target_value}</td><td>{p.max_value}</td><td>{p.test_method}</td><td><button type="button" onClick={() => removeParam(i)} className="btn btn-ghost btn-sm"><X size={14} color="#DC2626" /></button></td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-spec-btn">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QC Test Result Modal */}
      {showResultModal && (
        <div className="modal-overlay" data-testid="qc-result-modal">
          <div className="modal-content" style={{ maxWidth: '650px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>QC Test - {resultForm.product_sku}</h2></div>
            <form onSubmit={submitQcResult}>
              <div className="modal-body">
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>Enter actual values. Results will be auto-validated against spec ranges.</p>
                <div className="table-container">
                  <table>
                    <thead><tr><th>Parameter</th><th>Unit</th><th>Min</th><th>Target</th><th>Max</th><th>Actual Value *</th></tr></thead>
                    <tbody>
                      {resultForm.test_results.map((t, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 500 }}>{t.name}</td>
                          <td>{t.unit}</td>
                          <td className="tabular-nums">{t.min_value}</td>
                          <td className="tabular-nums">{t.target_value}</td>
                          <td className="tabular-nums">{t.max_value}</td>
                          <td>
                            <input type="text" className="form-input" value={t.actual_value} onChange={(e) => {
                              const updated = [...resultForm.test_results];
                              updated[i].actual_value = e.target.value;
                              setResultForm({ ...resultForm, test_results: updated });
                            }} required style={{ width: '100px' }} data-testid={`actual-${t.name}`} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="form-group" style={{ marginTop: '12px' }}><label className="form-label">Remarks</label><textarea className="form-textarea" value={resultForm.remarks} onChange={(e) => setResultForm({...resultForm, remarks: e.target.value})} rows={2} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowResultModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-qc-result-btn">Submit Test Result</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Specifications;
